import type { AxiosRequestHeaders, CancelToken } from 'axios';
import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { map, Observable } from 'rxjs';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { getDomain } from '@libs/utils';

function getRelativePath(): string {
  if (!(getDomain.isWhiteLabel || getDomain.isStenaLine)) {
    return '/d/';
  }

  return '/';
}

export function camelCased<T extends object>(
  options: camelcaseKeys.Options
): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>) =>
    source.pipe(map((input) => camelcaseKeys(input, options)));
}

export interface GetShortUrlResponse {
  shortUrl: string;
}

export class ApiQuery {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? apiUrl();
    if (this.baseUrl.endsWith('/')) {
      this.baseUrl = this.baseUrl.substring(0, this.baseUrl.length - 1);
    }
  }

  public async getShortUrl(longUrl: string): Promise<string> {
    const encodedUrl = encodeURIComponent(longUrl);
    const shortUrl = await axios.post<GetShortUrlResponse>(
      `https://admin.sem.bo/ShortUrl/Create?url=${encodedUrl}`,
      null,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );

    return shortUrl.data.shortUrl ?? longUrl;
  }

  public get<T>(
    endpointPath: string,
    headers?: AxiosRequestHeaders
  ): Observable<T> {
    return this.asObservable<T>(async (cancel) => {
      const result = await axios.get<T>(this.getEndpointUrl(endpointPath), {
        cancelToken: cancel,
        headers,
      });
      cancel.throwIfRequested();

      return result.data;
    });
  }

  // TODO: fix later
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async post<T>(endpointPath: string, data?: T): Promise<any> {
    const result = await axios.post(this.getEndpointUrl(endpointPath), data);
    return result.data;
  }

  public postObservable<T>(
    endpointPath: string,
    data?: unknown
  ): Observable<T> {
    return this.asObservable<T>(async (cancel) => {
      const result = await axios.post<T>(
        this.getEndpointUrl(endpointPath),
        data,
        {
          cancelToken: cancel,
        }
      );

      return result.data;
    });
  }

  public getEndpointUrl(endpointPath: string): string {
    let result = this.baseUrl;
    if (!endpointPath.startsWith('/')) {
      result += '/';
    }
    result += endpointPath;

    return result;
  }

  private asObservable<T>(
    taskProducer: (cancel: CancelToken) => Promise<T>
  ): Observable<T> {
    return new Observable<T>((observer) => {
      const cts = axios.CancelToken.source();
      let disposed = false;
      let finished = false;

      taskProducer(cts.token).then(
        (result) => {
          if (!disposed) {
            finished = true;
            observer.next(result);
            observer.complete();
          }
        },
        (error) => {
          observer.error(error);
        }
      );

      return () => {
        if (disposed || finished) {
          return;
        }

        disposed = true;
        cts.cancel();
      };
    });
  }
}

export function apiUrl(): string {
  if (getDomain.isProduction) {
    return getRelativePath();
  }

  if (getDomain.isLocalhost && isFeatureToggleActive('local-server')) {
    return '/';
  }

  return 'https://test.sembo.se/master';
}
